import { trigger, state, style } from "@angular/animations";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, AfterViewInit, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource, MatTable } from "@angular/material/table";
import { Subject } from "rxjs";
import { ErrorDialogComponent } from "src/app/dialog/error-dialog/error-dialog.component";
import { Environment } from "src/app/models/environment";
import { EnvironmentService } from "src/app/services/environment.service";
import { Alert } from "../models/alert";
import { Team } from "../models/team";
import { OnCallService } from "../services/oncall.service";
import { PagedAlertsResponse } from "../models/paged-alerts-response";
import { UserProfileService } from "src/app/services/user-profile.service";
import { LoadingService } from "src/app/services/loading.service";
import { ToastService } from "src/app/services/toast.service";

@Component({
    selector: 'app-oncall-alert',
    templateUrl: './alert.component.html',
    styleUrls: ['./alert.component.css', '../../home/home.component.css'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
            state('expanded', style({ height: '*' }))
        ]),
    ],
    standalone: false
})
export class AlertComponent implements AfterViewInit, OnInit, OnDestroy {
  environment: Environment;
  pageEnabled: boolean;
  pageSize: number = 10;
  cachedAlerts: Array<Alert> = [];
  cachedIndexes: number[] = [];
  teams: Array<Team>;
  displayedColumns: string[] = ['id', 'team', 'oncall', 'severity', 'description', 'created', 'status', 'actions'];
  columns: any[] = [];
  dataSource!: MatTableDataSource<Alert>;
  isPlatform: boolean = false;
  expandedRow: Alert | null = null;
  selectedStatus: string = "All";
  selectedTeam!: string;
  currentUserEmail!: string;
  continuationToken: string | null = '';
  private readonly _destroying$ = new Subject<void>();
  @ViewChild('alertsTable') alertsTable!: MatTable<any>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private readonly userProfileService: UserProfileService,
    private readonly environmentService: EnvironmentService,
    private onCallService: OnCallService,
    private dialog: MatDialog,
    private readonly loadingService: LoadingService,
    private readonly toast: ToastService
  ) {
    this.environment = this.environmentService.getEnvironment();
    this.pageEnabled = this.environment.featureToggles.onCallEnabled === 'true';
    this.teams = new Array<Team>();
  }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource<Alert>([]);
    this.columns = this.getColumns();

    // Get user email using the centralized method
    this.userProfileService.getUserEmail().subscribe(email => {
      this.currentUserEmail = email;
    });

    // Check if user is a platform team member
    this.userProfileService.isPlatformTeamMember().subscribe(isPlatformMember => {
      this.isPlatform = isPlatformMember;
    });

    this.getTeams();
  }

  ngAfterViewInit(): void {
    this.paginator.page.subscribe(this.pageChanged.bind(this));
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  applySorting() {
    if (this.dataSource) {
      this.dataSource.sort = this.sort;
      this.dataSource.sortingDataAccessor = (alert: Alert, header: string) => {
        switch (header) {
          case 'created':
            return new Date(alert.state.created);
          case 'id':
            return alert.alertDetails.id;
          case 'severity':
            return alert.alertDetails.data.essentials.severity.slice(-1);
          case 'team':
            return alert.state.targetTeam.name.toLowerCase();
          case 'description':
            return alert.alertDetails.data.essentials.alertRule.toLowerCase();
          case 'status':
            return this.getAlertStatus(alert.state.status).toLowerCase();
          default:
            return '';
        }
      };
    }
  }

  setDefaultTeamFilter(): void {
    let currentUsersTeam = this.teams.find((team) =>
      team.members.some((member) => member.id.toLocaleLowerCase() === this.currentUserEmail.toLocaleLowerCase()));
    this.selectedTeam = currentUsersTeam ? currentUsersTeam.name : 'All';
  }

  getTeams(): void {
    this.loadingService.show('alerts');
    this.teams = new Array<Team>();
    this.onCallService.getTeams()
      .subscribe({
        next: (teams) => {
          this.teams = teams;
          this.setDefaultTeamFilter();
          this.getAlerts();
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'Error fetching teams');
        },
      });
  }

  getAlerts(): void {
    this.loadingService.show('alerts');
    this.onCallService.getAlerts(this.selectedStatus, this.selectedTeam, this.paginator.pageSize, this.continuationToken!)
      .subscribe({
        next: (response: PagedAlertsResponse) => {
          this.cachedIndexes.push(this.paginator.pageIndex);
          this.cachedAlerts.push(...response.alerts);
          this.paginator.length = response.continuationToken == null ? this.cachedAlerts.length : this.cachedAlerts.length + 1;
          this.continuationToken = response.continuationToken;
          this.redrawTable();
          this.applySorting();
          this.loadingService.hide('alerts');
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'Error fetching alerts');
        },
      });
  }

  pageChanged(event: PageEvent): void {
    if (this.pageSize !== this.paginator.pageSize) {
      this.pageSize = this.paginator.pageSize;
      this.refreshAlerts();
      return;
    }

    if (event.previousPageIndex! < event.pageIndex && this.cachedIndexes.indexOf(event.pageIndex) === -1) {
      this.getAlerts();
    } else {
      this.redrawTable();
    }
  }

  redrawTable(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const end = start + this.paginator.pageSize;
    this.dataSource.data = this.cachedAlerts.slice(start, end);
  }

  refreshAlerts(): void {
    this.cachedAlerts = [];
    this.cachedIndexes = [];
    this.continuationToken = '';
    this.paginator.pageIndex = 0;
    this.getAlerts();
  }

  isAlertResolved(alert: Alert): boolean {
    return this.getAlertStatus(alert.state.status) === 'Resolved';
  }

  isAlertAcknowledgedOrResolved(alert: Alert): boolean {
    return this.getAlertStatus(alert.state.status) === 'Resolved' || this.getAlertStatus(alert.state.status) === 'Acknowledged';
  }

  disableAckEscResButtons(): boolean {
    return true;
  }

  performAlertAction(alert: Alert, action: string, event: Event): void {
    event.stopPropagation();
    this.onCallService.performAlertAction(alert.alertDetails.id, action)
      .subscribe({
        next: () => {
          this.refreshAlerts();
          this.toast.success(`Alert ${alert.alertDetails.id} updated`);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, `Error`);
        },
      });
  }

  toggleRow(alert: Alert) {
    if (this.expandedRow === alert) {
      this.expandedRow = null;
    } else {
      this.expandedRow = alert;
    }
  }

  handleError(error: HttpErrorResponse, message: string): void {
    this.loadingService.hide('alerts');
    this.openErrorDialog(`${message}: ${error.status} ${error.statusText}`);
  }

  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      data: { message: errorMessage }
    });
  }

  getShortIdString(id: string): string {
    return id.slice(0, 3) + '...' + id.slice(-3);
  }

  getColumns(): any[] {
    let columns =
      [
        {
          columnDef: 'id',
          header: 'Id',
          cell: (alert: Alert) => `${this.getShortIdString(alert.alertDetails.id)}`
        },
        {
          columnDef: 'team',
          header: 'Team',
          cell: (alert: Alert) => `${alert.state.targetTeam.name}`
        },
        {
          columnDef: 'oncall',
          header: 'Assigned To',
          cell: (alert: Alert) => !alert.state.targetTeam.splunkOnCallIntegrated ? `${alert.state.targetTeam.onCallTeamMember.id}` : ''
        },
        {
          columnDef: 'severity',
          header: 'Severity',
          cell: (alert: Alert) => `${alert.alertDetails.data.essentials.severity.slice(-1)}`
        },
        {
          columnDef: 'description',
          header: 'Description',
          cell: (alert: Alert) => `${alert.alertDetails.data.essentials.alertRule}`
        },
        {
          columnDef: 'created',
          header: 'Created (UTC)',
          cell: (alert: Alert) => `${this.formatDate(alert.state.created)}`
        },
        {
          columnDef: 'status',
          header: 'Status',
          cell: (alert: Alert) => `${this.getAlertStatus(alert.state.status)}`
        }
      ]
    return columns;
  }

  getAlertStatus(status: number): string {
    switch (status) {
      case 0:
        return 'Triggered';
      case 1:
        return 'Acknowledged';
      case 2:
        return 'Resolved';
      case 3:
        return 'Suppressed';
      case 4:
        return 'Forwarded';
      default:
        return 'Unknown';
    }
  }

  getStatusColor(status: number): string {
    switch (status) {
      case 0:
        return 'red';
      case 1:
        return 'orange';
      case 2:
        return 'green';
      default:
        return 'black';
    }
  }

  formatDate(date: string): string {
    const dateObject = new Date(date);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: 'UTC'
    };

    return dateObject.toLocaleString('en-US', options);
  }

  onCopy(copied: boolean): void {
    if (copied) {
      this.toast.info('Alert Id copied to clipboard');
    }
  }
}
